// src/pages/hr/recruitment/CandidateList.jsx
import React, { useState, useRef } from "react";
import { useRecruitmentContext } from "../../../context/RecruitmentContext";
import { createCandidate, deleteCandidate } from "../../../hooks/useRecruitment";
import { useAuth } from "../../../context/AuthContext";
import CandidateProfile from "./CandidateProfile";
import { storage } from "../../../firebase/config";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { toast } from "react-hot-toast";
import { Search, X, Download, Plus, UserPlus, Upload, FileText, Eye, Trash2, RefreshCw } from "lucide-react";
import { EMS_DEPARTMENTS } from "../../../utils/hrConstants";

export default function CandidateList() {
  const { candidates, candidatesLoading, jobOpenings, stages, stageColors } = useRecruitmentContext();
  const { userProfile } = useAuth();

  const [search, setSearch] = useState("");
  const [filterStage, setFilterStage] = useState("all");
  const [filterJob, setFilterJob] = useState("all");

  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Resume Upload State & Ref
  const fileInputRef = useRef(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [resumePreviewUrl, setResumePreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  // Resume input mode: 'upload' (Firebase Storage) or 'link' (Google Drive / OneDrive / Dropbox)
  const [resumeInputMode, setResumeInputMode] = useState("upload");
  const [resumeLinkInput, setResumeLinkInput] = useState("");

  // Open job openings for the dropdown (only Open status)
  const openJobs = jobOpenings.filter((j) => j.status === "Open" || j.status === "open");

  const [jobSearch, setJobSearch] = useState("");
  const [showJobDropdown, setShowJobDropdown] = useState(false);

  const filteredJobs = openJobs.filter((j) =>
    (j.title || "").toLowerCase().includes(jobSearch.toLowerCase()) ||
    (j.department || "").toLowerCase().includes(jobSearch.toLowerCase())
  );

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    appliedPosition: "",
    jobId: "",
    department: "",
    hiringManager: "",
    experience: "3 years",
    source: "Direct",
    stage: "Applied",
  });

  function handleJobSelect(job) {
    setFormData({
      ...formData,
      jobId: job.id,
      appliedPosition: job.title,
      department: job.department || "",
      hiringManager: job.hiringManager || job.hiringManagerName || "",
    });
    setJobSearch(job.title);
    setShowJobDropdown(false);
  }

  const filtered = candidates.filter((c) => {
    const matchSearch =
      (c.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.email || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.appliedPosition || c.role || "").toLowerCase().includes(search.toLowerCase());
    const matchStage = filterStage === "all" || c.stage === filterStage;
    const matchJob = filterJob === "all" || c.jobId === filterJob;
    return matchSearch && matchStage && matchJob;
  });

  // Handle File Selection & 10MB Validation
  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;

    // Validate File Extension (.pdf, .doc, .docx)
    const allowedExts = [".pdf", ".doc", ".docx"];
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!allowedExts.includes(ext)) {
      toast.error("Invalid file format! Please upload a PDF, DOC, or DOCX resume.");
      e.target.value = "";
      return;
    }

    // Validate Max Size (10 MB = 10 * 1024 * 1024 bytes)
    const maxSizeBytes = 10 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      toast.error("File size exceeds 10 MB limit! Please select a smaller resume file.");
      e.target.value = "";
      return;
    }

    setResumeFile(file);
    setResumePreviewUrl(URL.createObjectURL(file));
    toast.success(`Resume selected: ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB)`);
  }

  function handleRemoveFile() {
    setResumeFile(null);
    setResumePreviewUrl("");
  }

  async function handleAddSubmit(e) {
    if (e && e.preventDefault) e.preventDefault();
    if (uploading) return;

    if (!formData.name || !formData.name.trim()) { toast.error("Full Name is required!"); return; }
    if (!formData.email || !formData.email.trim()) { toast.error("Email Address is required!"); return; }
    if (!formData.appliedPosition || !formData.appliedPosition.trim()) { toast.error("Applied Position is required!"); return; }

    // Validate resume: file or link must be provided
    if (resumeInputMode === "upload" && !resumeFile) {
      toast.error("Please upload a resume (PDF, DOC, or DOCX) or switch to 'Paste Link' mode.");
      return;
    }
    if (resumeInputMode === "link" && !resumeLinkInput.trim()) {
      toast.error("Please paste a valid resume link (Google Drive, OneDrive, Dropbox, etc.).");
      return;
    }

    setUploading(true);
    let resumeUrl = "";
    let resumeFileName = "";
    let resumeStoragePath = "";
    let resumeUploadType = resumeInputMode; // 'upload' | 'link'

    try {
      if (resumeInputMode === "upload" && resumeFile) {
        toast.loading("Uploading resume to Firebase Storage...", { id: "uploadToast" });
        try {
          // Step 1: Create candidate doc first to get the candidateId
          const tempCandidateId = `tmp_${Date.now()}`;
          const ext = resumeFile.name.substring(resumeFile.name.lastIndexOf("."));
          const safeFileName = resumeFile.name.replace(/[^a-zA-Z0-9._-]/g, "_");
          resumeStoragePath = `candidate-resumes/${tempCandidateId}/${safeFileName}`;
          const storageRef = ref(storage, resumeStoragePath);
          await uploadBytes(storageRef, resumeFile, {
            contentType: resumeFile.type || "application/octet-stream",
            customMetadata: { uploadedBy: userProfile?.displayName || "HR Admin", candidateName: formData.name.trim() },
          });
          resumeUrl = await getDownloadURL(storageRef);
          resumeFileName = resumeFile.name;
          toast.dismiss("uploadToast");
          toast.success("Resume uploaded to Firebase Storage!", { duration: 2000 });
        } catch (storageErr) {
          toast.dismiss("uploadToast");
          console.warn("[ResumeUpload] Firebase Storage error:", storageErr);
          toast.error("Storage upload failed. Please try again or use 'Paste Link' mode.");
          setUploading(false);
          return;
        }
      } else if (resumeInputMode === "link") {
        resumeUrl = resumeLinkInput.trim();
        resumeFileName = "Linked Resume";
        resumeStoragePath = "";
      }

      // Save Candidate document in Firestore
      const candidateId = await createCandidate({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: (formData.phone || "").trim(),
        appliedPosition: formData.appliedPosition.trim(),
        experience: (formData.experience || "").trim() || "0 years",
        department: formData.department || "Engineering",
        source: formData.source || "Direct Application",
        jobId: formData.jobId || null,
        stage: "Applied",
        leadRecommendation: "Pending",
        resumeUrl,
        resumeFileName,
        resumeStoragePath,
        resumeUploadType,
        resumeUploadedAt: new Date().toISOString(),
        createdByName: userProfile?.displayName || userProfile?.name || "HR Admin",
      });

      // If uploaded to storage, move file to the proper candidateId-based path
      if (resumeInputMode === "upload" && candidateId && resumeFile) {
        try {
          const ext = resumeFile.name.substring(resumeFile.name.lastIndexOf("."));
          const safeFileName = resumeFile.name.replace(/[^a-zA-Z0-9._-]/g, "_");
          const properPath = `candidate-resumes/${candidateId}/${safeFileName}`;
          const properRef = ref(storage, properPath);
          await uploadBytes(properRef, resumeFile, {
            contentType: resumeFile.type || "application/octet-stream",
            customMetadata: { uploadedBy: userProfile?.displayName || "HR Admin", candidateName: formData.name.trim(), candidateId },
          });
          const properUrl = await getDownloadURL(properRef);
          // Update Firestore with correct path and URL
          const { doc: firestoreDoc, updateDoc: firestoreUpdate } = await import("firebase/firestore");
          const { db: firestoreDb } = await import("../../../firebase/config");
          await firestoreUpdate(firestoreDoc(firestoreDb, "candidates", candidateId), {
            resumeUrl: properUrl,
            resumeStoragePath: properPath,
          });
        } catch (_) {
          // Non-fatal: original upload URL is still valid
          console.warn("[ResumeUpload] Could not re-upload to candidateId path, using temporary path.");
        }
      }

      toast.success("Candidate profile created successfully!");
      setShowAddModal(false);
      resetForm();
    } catch (err) {
      toast.dismiss("uploadToast");
      console.error("[CandidateCreationError]", err);
      toast.error("Failed to create candidate profile: " + err.message);
    } finally {
      setUploading(false);
    }
  }

  function resetForm() {
    setFormData({
      name: "",
      email: "",
      phone: "",
      appliedPosition: "",
      jobId: "",
      department: "",
      hiringManager: "",
      experience: "3 years",
      source: "Direct",
      stage: "Applied",
    });
    setJobSearch("");
    setShowJobDropdown(false);
    setResumeFile(null);
    setResumePreviewUrl("");
    setResumeInputMode("upload");
    setResumeLinkInput("");
  }

  async function handleDeleteCandidate(c) {
    if (!window.confirm(`Are you sure you want to permanently delete candidate "${c.name}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await deleteCandidate(c.id, c.jobId);
      toast.success(`Candidate ${c.name} deleted successfully.`);
      if (selectedCandidate?.id === c.id) {
        setSelectedCandidate(null);
      }
    } catch (err) {
      toast.error("Failed to delete candidate: " + err.message);
    }
  }

  function handleExportCSV() {
    const headers = ["Name", "Email", "Phone", "Applied Position", "Stage", "Lead Recommendation", "Experience", "Department", "Source"];
    const rows = filtered.map((c) => [
      `"${c.name || ""}"`,
      `"${c.email || ""}"`,
      `"${c.phone || ""}"`,
      `"${c.appliedPosition || c.role || ""}"`,
      `"${c.stage || ""}"`,
      `"${c.leadRecommendation || "Pending"}"`,
      `"${c.experience || ""}"`,
      `"${c.department || ""}"`,
      `"${c.source || ""}"`,
    ]);
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `candidates_export_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <style>{`
        .cand-table { width: 100%; border-collapse: collapse; font-size: 0.84rem; background: var(--bg-card, #fff); border-radius: 12px; overflow: hidden; border: 1px solid var(--border, #e5e7eb); box-shadow: 0 1px 5px rgba(0,0,0,0.05); }
        .cand-table th { background: var(--bg-secondary, #f9fafb); text-align: left; padding: 0.75rem 0.9rem; font-size: 0.74rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid var(--border, #e5e7eb); }
        .cand-table td { padding: 0.75rem 0.9rem; border-bottom: 1px solid var(--border, #f3f4f6); vertical-align: middle; color: var(--text-primary, #111); }
        .cand-table tr:hover td { background: var(--bg-secondary, #f9fafb); cursor: pointer; }
        .stage-pill { display: inline-block; padding: 0.2rem 0.65rem; border-radius: 99px; font-size: 0.72rem; font-weight: 700; color: #fff; }
        .score-bar-bg { height: 6px; width: 60px; background: #e5e7eb; border-radius: 99px; overflow: hidden; display: inline-block; vertical-align: middle; margin-right: 6px; }
        .score-bar-fill { height: 100%; background: #10b981; border-radius: 99px; }

        /* Modern ATS Toolbar & Search Box */
        .ats-toolbar { display: flex; justify-content: space-between; align-items: center; gap: 0.85rem; flex-wrap: wrap; margin-bottom: 0.25rem; }
        .ats-search-box {
          position: relative;
          display: flex;
          align-items: center;
          flex: 1;
          min-width: 280px;
          max-width: 460px;
          background: var(--bg-card, #ffffff);
          border: 1.5px solid var(--border, #e2e8f0);
          border-radius: 10px;
          padding: 0 0.85rem;
          height: 42px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          transition: all 0.2s ease-in-out;
        }
        .ats-search-box:focus-within {
          border-color: #1b4b31;
          box-shadow: 0 0 0 3.5px rgba(27, 75, 49, 0.12), 0 2px 8px rgba(27, 75, 49, 0.06);
          background: #ffffff;
        }
        .ats-search-icon {
          color: #64748b;
          flex-shrink: 0;
          margin-right: 0.65rem;
          transition: color 0.2s ease;
        }
        .ats-search-box:focus-within .ats-search-icon {
          color: #1b4b31;
        }
        .ats-search-input {
          width: 100%;
          border: none;
          outline: none;
          background: transparent;
          font-size: 0.86rem;
          font-weight: 500;
          color: var(--text-primary, #0f172a);
          padding: 0;
        }
        .ats-search-input::placeholder {
          color: #94a3b8;
          font-weight: 400;
        }
        .ats-search-clear {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: none;
          background: #e2e8f0;
          color: #475569;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          cursor: pointer;
          padding: 0;
          margin-left: 0.5rem;
          transition: all 0.15s ease;
          flex-shrink: 0;
        }
        .ats-search-clear:hover {
          background: #cbd5e1;
          color: #0f172a;
        }
        .ats-toolbar-actions { display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap; }
        .ats-select {
          height: 42px;
          padding: 0 0.85rem;
          border: 1.5px solid var(--border, #e2e8f0);
          border-radius: 10px;
          font-size: 0.85rem;
          font-weight: 500;
          background-color: var(--bg-card, #fff);
          color: var(--text-primary, #1e293b);
          outline: none;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 1px 2px rgba(0,0,0,0.03);
        }
        .ats-select:hover { border-color: #cbd5e1; }
        .ats-select:focus { border-color: #1b4b31; box-shadow: 0 0 0 3px rgba(27, 75, 49, 0.12); }
        .ats-action-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          height: 42px;
          padding: 0 1.1rem;
          border-radius: 10px;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.2s ease;
        }
        .btn-primary {
          background: #1b4b31;
          color: #ffffff;
          border: 1px solid #1b4b31;
          box-shadow: 0 2px 5px rgba(27, 75, 49, 0.2);
        }
        .btn-primary:hover {
          background: #143824;
          border-color: #143824;
          box-shadow: 0 4px 10px rgba(27, 75, 49, 0.3);
          transform: translateY(-1px);
        }
        .btn-secondary {
          background: #ffffff;
          color: #334155;
          border: 1.5px solid #e2e8f0;
          box-shadow: 0 1px 2px rgba(0,0,0,0.03);
        }
        .btn-secondary:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
          color: #0f172a;
        }

        /* Modal Overlay & Backdrop */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(15, 23, 42, 0.65);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 1.25rem;
          box-sizing: border-box;
          animation: atsFadeIn 0.2s ease-out;
        }

        @keyframes atsFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .modal-body {
          background: #ffffff;
          border-radius: 16px;
          width: 100%;
          max-width: 620px;
          max-height: 90vh;
          overflow-y: auto;
          padding: 1.75rem 2rem;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.08);
          border: 1px solid var(--border, #e2e8f0);
          box-sizing: border-box;
          position: relative;
          animation: atsSlideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1);
        }

        @keyframes atsSlideUp {
          from { opacity: 0; transform: translateY(16px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 1.1rem;
          border-bottom: 1px solid #f1f5f9;
          margin-bottom: 1.25rem;
        }

        .modal-title-group {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .modal-title-icon {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: #e6f4ed;
          color: #1b4b31;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .modal-title {
          margin: 0;
          font-size: 1.15rem;
          font-weight: 700;
          color: #0f172a;
          line-height: 1.2;
        }

        .modal-subtitle {
          margin: 3px 0 0 0;
          font-size: 0.78rem;
          color: #64748b;
        }

        .modal-close-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          color: #64748b;
          cursor: pointer;
          transition: all 0.15s ease;
          padding: 0;
        }

        .modal-close-btn:hover {
          background: #fee2e2;
          color: #ef4444;
          border-color: #fca5a5;
        }

        .form-grid-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.1rem;
        }

        @media (max-width: 600px) {
          .form-grid-2 {
            grid-template-columns: 1fr;
          }
        }

        .form-field {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
        }

        .form-label {
          font-size: 0.8rem;
          font-weight: 600;
          color: #334155;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .form-label .required {
          color: #ef4444;
        }

        .modal-body .ats-input, 
        .modal-body .ats-select {
          width: 100%;
          box-sizing: border-box;
          height: 42px;
          padding: 0 0.85rem;
          border: 1.5px solid #cbd5e1;
          border-radius: 9px;
          font-size: 0.86rem;
          font-weight: 500;
          background: #ffffff;
          color: #0f172a;
          outline: none;
          transition: all 0.2s ease;
        }

        .modal-body .ats-input:focus, 
        .modal-body .ats-select:focus {
          border-color: #1b4b31;
          box-shadow: 0 0 0 3.5px rgba(27, 75, 49, 0.12);
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          gap: 0.75rem;
          margin-top: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid #f1f5f9;
        }
      `}</style>

      {/* Toolbar */}
      <div className="ats-toolbar">
        <div className="ats-search-box">
          <Search size={18} className="ats-search-icon" />
          <input
            type="text"
            className="ats-search-input"
            placeholder="Search candidate by name, position, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className="ats-search-clear"
              onClick={() => setSearch("")}
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="ats-toolbar-actions">
          <select className="ats-select" value={filterStage} onChange={(e) => setFilterStage(e.target.value)}>
            <option value="all">All Stages</option>
            {stages.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          <select className="ats-select" value={filterJob} onChange={(e) => setFilterJob(e.target.value)}>
            <option value="all">All Jobs</option>
            {jobOpenings.map((j) => (
              <option key={j.id} value={j.id}>{j.title}</option>
            ))}
          </select>

          <button className="btn-secondary ats-action-btn" onClick={handleExportCSV}>
            <Download size={15} /> Export CSV
          </button>
          <button className="btn-primary ats-action-btn" onClick={() => setShowAddModal(true)}>
            <Plus size={16} /> Add Candidate
          </button>
        </div>
      </div>

      {/* Candidates Table */}
      {candidatesLoading ? (
        <div style={{ textAlign: "center", padding: "3rem", color: "var(--text-muted)" }}>Loading Candidates...</div>
      ) : filtered.length === 0 ? (
        <div style={{ background: "var(--bg-card, #fff)", border: "1px dashed var(--border)", borderRadius: 12, padding: "3rem", textAlign: "center", color: "var(--text-muted)" }}>
          No candidates found matching criteria.
        </div>
      ) : (
        <table className="cand-table">
          <thead>
            <tr>
              <th>Candidate</th>
              <th>Applied Position</th>
              <th>Stage</th>
              <th>Lead Recommendation</th>
              <th>Experience</th>
              <th>Source</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} onClick={() => setSelectedCandidate(c)}>
                <td>
                  <div style={{ fontWeight: 700, color: "var(--text-primary)" }}>{c.name}</div>
                  <div style={{ fontSize: "0.74rem", color: "var(--text-muted)" }}>{c.email}</div>
                </td>
                <td style={{ fontWeight: 600 }}>{c.appliedPosition || c.role || "—"}</td>
                <td>
                  <span className="stage-pill" style={{ background: stageColors[c.stage] || "#1b4b31" }}>
                    {c.stage || "Applied"}
                  </span>
                </td>
                <td>
                  {c.leadRecommendation ? (
                    <span style={{
                      padding: "0.2rem 0.55rem", borderRadius: 99, fontSize: "0.72rem", fontWeight: 700,
                      background: c.leadRecommendation === "Proceed to Technical Interview" ? "#d1fae5" : c.leadRecommendation === "Reject" ? "#fee2e2" : "#fef3c7",
                      color: c.leadRecommendation === "Proceed to Technical Interview" ? "#065f46" : c.leadRecommendation === "Reject" ? "#991b1b" : "#92400e"
                    }}>
                      {c.leadRecommendation === "Proceed to Technical Interview" ? "✔ Proceed" : c.leadRecommendation}
                    </span>
                  ) : (
                    <span style={{ fontSize: "0.74rem", color: "var(--text-muted)", fontStyle: "italic" }}>
                      {c.assignedLeadName ? `Awaiting ${c.assignedLeadName}` : "Unassigned"}
                    </span>
                  )}
                </td>
                <td>{c.experience || "—"}</td>
                <td>
                  <span style={{ background: "var(--bg-secondary)", padding: "0.15rem 0.5rem", borderRadius: 4, fontSize: "0.74rem", color: "var(--text-muted)" }}>
                    {c.source || "Direct"}
                  </span>
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                    <button className="btn-secondary" style={{ padding: "0.25rem 0.6rem", fontSize: "0.75rem" }} onClick={() => setSelectedCandidate(c)}>
                      View Profile
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ padding: "0.25rem 0.5rem", fontSize: "0.75rem", color: "#ef4444", borderColor: "#fca5a5" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCandidate(c);
                      }}
                      title="Permanently delete candidate"
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Candidate Profile Modal */}
      {selectedCandidate && (
        <CandidateProfile
          candidate={selectedCandidate}
          onClose={() => setSelectedCandidate(null)}
        />
      )}

      {/* Add Candidate Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-body" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <div className="modal-title-icon">
                  <UserPlus size={20} />
                </div>
                <div>
                  <h3 className="modal-title">Add Candidate Profile</h3>
                  <p className="modal-subtitle">Enter applicant details to add them to the recruitment pipeline</p>
                </div>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowAddModal(false)}
                title="Close"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleAddSubmit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div className="form-grid-2">
                <div className="form-field">
                  <label className="form-label">
                    Full Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="ats-input"
                    required
                    placeholder="e.g. John Doe"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">
                    Email Address <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    className="ats-input"
                    required
                    placeholder="e.g. john@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-field">
                  <label className="form-label">Phone Number</label>
                  <input
                    type="text"
                    className="ats-input"
                    placeholder="e.g. +1 555-0192"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div className="form-field" style={{ position: "relative" }}>
                  <label className="form-label">
                    Applied Position <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="ats-input"
                    placeholder="Search or select a job opening..."
                    value={jobSearch}
                    onChange={(e) => {
                      setJobSearch(e.target.value);
                      setShowJobDropdown(true);
                      setFormData({ ...formData, appliedPosition: e.target.value, jobId: "", department: "", hiringManager: "" });
                    }}
                    onFocus={() => setShowJobDropdown(true)}
                    onBlur={() => setTimeout(() => setShowJobDropdown(false), 200)}
                    autoComplete="off"
                  />
                  {showJobDropdown && (
                    <div style={{
                      position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                      background: "#fff", border: "1.5px solid #1b4b31", borderRadius: 10,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 9999,
                      maxHeight: 220, overflowY: "auto",
                    }}>
                      {filteredJobs.length === 0 ? (
                        <div style={{ padding: "0.75rem 1rem", fontSize: "0.8rem", color: "var(--text-muted)", textAlign: "center" }}>
                          {openJobs.length === 0 ? "No open job postings available." : "No matching jobs found."}
                        </div>
                      ) : (
                        filteredJobs.map((job) => (
                          <div
                            key={job.id}
                            onMouseDown={() => handleJobSelect(job)}
                            style={{
                              padding: "0.65rem 1rem", cursor: "pointer",
                              borderBottom: "1px solid #f1f5f9",
                              transition: "background 0.15s",
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = "#f0fdf4"}
                            onMouseLeave={(e) => e.currentTarget.style.background = ""}
                          >
                            <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#0f172a" }}>{job.title}</div>
                            <div style={{ fontSize: "0.74rem", color: "#64748b", marginTop: 2 }}>
                              {job.department} {job.hiringManager ? `· Manager: ${job.hiringManager}` : ""}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-grid-2">
                <div className="form-field">
                  <label className="form-label">Total Experience</label>
                  <input
                    type="text"
                    className="ats-input"
                    placeholder="e.g. 4 years"
                    value={formData.experience}
                    onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Department</label>
                  {formData.jobId ? (
                    <input
                      type="text"
                      className="ats-input"
                      value={formData.department}
                      readOnly
                      style={{ background: "#f1f5f9", color: "#64748b", cursor: "not-allowed" }}
                    />
                  ) : (
                    <select
                      className="ats-select"
                      value={formData.department || ""}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    >
                      <option value="">Select Department</option>
                      {EMS_DEPARTMENTS.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="form-field">
                  <label className="form-label">Application Source</label>
                  <select
                    className="ats-select"
                    value={formData.source}
                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                  >
                    <option value="Direct">Direct Application</option>
                    <option value="Employee Referral">Employee Referral</option>
                    <option value="LinkedIn">LinkedIn</option>
                    <option value="Naukri">Naukri</option>
                    <option value="Indeed">Indeed</option>
                    <option value="Campus">Campus Drive</option>
                  </select>
                </div>
              </div>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />

              {/* Resume Section */}
              <div className="form-field">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                  <label className="form-label" style={{ margin: 0 }}>
                    Candidate Resume <span className="required">*</span>
                  </label>
                  {/* Mode Toggle */}
                  <div style={{ display: "flex", borderRadius: 8, overflow: "hidden", border: "1.5px solid #d1d5db", fontSize: "0.74rem" }}>
                    <button
                      type="button"
                      onClick={() => { setResumeInputMode("upload"); setResumeLinkInput(""); }}
                      style={{
                        padding: "0.22rem 0.75rem", border: "none", cursor: "pointer", fontWeight: 600,
                        background: resumeInputMode === "upload" ? "#1b4b31" : "#f8fafc",
                        color: resumeInputMode === "upload" ? "#fff" : "#64748b",
                        transition: "all 0.15s",
                      }}
                    >
                      ⬆ Upload File
                    </button>
                    <button
                      type="button"
                      onClick={() => { setResumeInputMode("link"); setResumeFile(null); setResumePreviewUrl(""); }}
                      style={{
                        padding: "0.22rem 0.75rem", border: "none", cursor: "pointer", fontWeight: 600,
                        background: resumeInputMode === "link" ? "#1b4b31" : "#f8fafc",
                        color: resumeInputMode === "link" ? "#fff" : "#64748b",
                        transition: "all 0.15s",
                      }}
                    >
                      🔗 Paste Link
                    </button>
                  </div>
                </div>

                {/* --- Upload Mode --- */}
                {resumeInputMode === "upload" && (
                  !resumeFile ? (
                    <div
                      style={{
                        border: "2px dashed #cbd5e1", borderRadius: 10, padding: "1.25rem",
                        textAlign: "center", background: "#f8fafc", cursor: "pointer",
                        transition: "border-color 0.2s",
                      }}
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = "#1b4b31"; }}
                      onDragLeave={(e) => { e.currentTarget.style.borderColor = "#cbd5e1"; }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.style.borderColor = "#cbd5e1";
                        const file = e.dataTransfer.files[0];
                        if (file) handleFileChange({ target: { files: [file], value: "" } });
                      }}
                    >
                      <Upload size={28} style={{ color: "#1b4b31", marginBottom: 6 }} />
                      <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "#0f172a" }}>
                        Click or drag &amp; drop to upload resume
                      </div>
                      <div style={{ fontSize: "0.74rem", color: "var(--text-muted)", marginTop: 2 }}>
                        PDF, DOC, or DOCX · Max 10 MB · Stored in Firebase Storage
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: 10, padding: "0.75rem 1rem" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.65rem" }}>
                        <FileText size={22} style={{ color: "#065f46", flexShrink: 0 }} />
                        <div>
                          <div style={{ fontWeight: 700, fontSize: "0.86rem", color: "#065f46" }}>{resumeFile.name}</div>
                          <div style={{ fontSize: "0.72rem", color: "#166534" }}>
                            {(resumeFile.size / (1024 * 1024)).toFixed(2)} MB · Will upload to Firebase Storage
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                        {resumePreviewUrl && (
                          <a href={resumePreviewUrl} target="_blank" rel="noreferrer"
                            className="btn-secondary"
                            style={{ padding: "0.25rem 0.55rem", fontSize: "0.72rem", textDecoration: "none", color: "#0284c7" }}
                            title="Preview local file before upload"
                          >
                            <Eye size={13} /> Preview
                          </a>
                        )}
                        <button type="button" className="btn-secondary"
                          style={{ padding: "0.25rem 0.55rem", fontSize: "0.72rem" }}
                          onClick={() => fileInputRef.current && fileInputRef.current.click()}
                          title="Replace resume file"
                        >
                          <RefreshCw size={13} /> Replace
                        </button>
                        <button type="button" className="btn-secondary"
                          style={{ padding: "0.25rem 0.55rem", fontSize: "0.72rem", color: "#ef4444", borderColor: "#fca5a5" }}
                          onClick={handleRemoveFile}
                          title="Remove resume file"
                        >
                          <Trash2 size={13} /> Remove
                        </button>
                      </div>
                    </div>
                  )
                )}

                {/* --- Link Mode --- */}
                {resumeInputMode === "link" && (
                  <div>
                    <input
                      type="url"
                      className="ats-input"
                      placeholder="Paste Google Drive, OneDrive, Dropbox, or direct link..."
                      value={resumeLinkInput}
                      onChange={(e) => setResumeLinkInput(e.target.value)}
                      style={{ marginBottom: "0.4rem" }}
                    />
                    <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>
                      💡 Ensure the link is publicly accessible or set to "anyone with link can view".
                      {resumeLinkInput.trim() && (
                        <a href={resumeLinkInput.trim()} target="_blank" rel="noreferrer"
                          style={{ marginLeft: "0.5rem", color: "#0284c7", fontWeight: 600 }}
                        >Test Link ↗</a>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary ats-action-btn"
                  onClick={() => setShowAddModal(false)}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={uploading}
                  className="btn-primary ats-action-btn"
                  style={{ opacity: uploading ? 0.7 : 1, cursor: uploading ? "wait" : "pointer" }}
                  onClick={handleAddSubmit}
                >
                  {uploading ? (
                    <>Creating Candidate...</>
                  ) : (
                    <>
                      <UserPlus size={16} /> Add Candidate
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
